import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { Fragment, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getRealm } from "../../../../../../AuthProvider";
import { FlexCenterWrapper } from "../../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../../components/Flex";
import LoadingOverlay from "../../../../../../components/LoadingOverlay";
import { ScrollArea } from "../../../../../../components/ScrollBar";
import Text from "../../../../../../components/Text";
import { useQueryParams } from "../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import AllScreeningAssessment from "../../../../../../pages/app/provider/scheduling/appointment/encounter/Assessment/AllScreeningAssessment";
import {
  DataItem,
  TableData,
} from "../../../../../../pages/app/provider/scheduling/appointment/encounter/Assessment/AssessmentDrawer";
import { useGetAllScreeningListPrefill } from "../../../../../../queries/encounter/screening/use-getAllScreenerAns";
import { useScreeningList } from "../../../../../../queries/encounter/screening/use-screening-get-list";
import { useScreeningUpdate } from "../../../../../../queries/encounter/screening/use-screening-update";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../utils/utils";
import { SCREENING_PREFILL_PAGE_SIZE } from "@/pages/app/provider/scheduling/appointment/encounter/Assessment/assessment-constant";

const noLoadQnImg = "/default-images/noLoadQn.svg";

interface ScreeningFullViewFormProps {
  activeTab: { ind: number; val: string };
  onSave: (ind: number) => void;
  onSkip: (ind: number, prev: boolean) => void;
  isPublicView?: boolean;
  appointmentId?: string;
  patientId?: string;
  assessmentTabs?: string[];
  actionId?: string;
}

export const ScreeningFullViewForm = ({
  activeTab,
  onSave,
  onSkip,
  isPublicView,
  appointmentId: appointmentIdProp,
  patientId: patientIdProp,
  assessmentTabs: assessmentTabsProp,
  /**
   * @see actionId represent the patient portal side `REQUIRING ACTION`
   */
  actionId: actionIdProp,
}: ScreeningFullViewFormProps) => {
  const queryClient = useQueryClient();
  const assessmentTab = useQueryParams("assessments");
  const providerRole = useQueryParams("providerRole");
  const providerName = useQueryParams("providerName");

  const assessmentTabs = assessmentTabsProp ?? assessmentTab?.split(",");

  const hostNameArr = location.hostname?.split(".");
  const xTENANTID = getRealm(hostNameArr);

  const { data, isLoading, isRefetching } = useScreeningList({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    isPublicView,
    type: activeTab.val,
    xTENANTID: isPublicView ? xTENANTID : getXtenantId(),
  });
  const paramAppointmentId = useSearchParams()[0].get("appointmentId");
  const paramPatientId = useSearchParams()[0].get("patientId");

  const appointmentid = appointmentIdProp ?? paramAppointmentId;
  const patientid = patientIdProp ?? paramPatientId;

  const showSnackbar = useSnackbar();
  const [tableData, setTableData] = useState<TableData>(data?.data);

  // 'submittedBy'
  // 'providerName'
  // 'providerRole'
  // 'patientDob'

  // const { mutateAsync: saveScreeningAns } = useScreeningCreate(isPublicView);
  const { mutateAsync: updateScreeningAns } = useScreeningUpdate(isPublicView);

  const { data: prefillData, refetch } = useGetAllScreeningListPrefill({
    type: activeTab.val,
    // to get all the data in one go
    pagination: {
      page: 0,
      pageSize: SCREENING_PREFILL_PAGE_SIZE,
    },
    isPublicView,
    gridSorting: [],
    patientId: appointmentid ? undefined : patientid!,
    appointmentUuid: appointmentid!,
    xTENANTID: isPublicView ? xTENANTID : getXtenantId(),
  });

  // const isPrefillDataAvailable =
  //   prefillData?.data?.content && prefillData?.data?.content?.length > 0;
  // console.log(
  //   "val here check ",
  //   prefillData?.data?.content,
  //   "ccc",
  //   data?.data,
  //   "abc",
  //   prefillData?.data?.content?.length > 0
  // );

  useEffect(() => {
    const transformedPrefillTableData: TableData = {
      answerOptions: data?.data?.answerOptions,
      screeners: data?.data?.screeners?.map((it: any, ind: number) => ({
        ...it,
        answerOptions: prefillData?.data?.content?.[ind]?.answer,
      })) as DataItem[],
    };

    // if (isPrefillDataAvailable) {
    setTableData(transformedPrefillTableData);
    // }
  }, [data?.data, prefillData?.data]);

  const handleRadioChange = (ind: number, val: string) => {
    const cloneTableData = cloneDeep(tableData);

    if (cloneTableData?.screeners?.[ind])
      cloneTableData.screeners[ind].answerOptions = val;

    setTableData(cloneTableData);
  };

  const saveHandler = () => {
    if (appointmentid || patientid) {
      const patientScreener = {
        screenerQuestion: tableData?.screeners
          // ?.filter((it) => it?.answerOptions)
          ?.map((it) => ({
            question: it?.uuid,
            answer: it?.answerOptions,
          })),
        appointmentId: appointmentid!,
        screenerType: activeTab.val as any,
        actionId: actionIdProp ?? undefined,
      };

      const payload = {
        patientUuid: patientid!,
        patientScreener,
        xTENANTID: isPublicView ? xTENANTID : getXtenantId(),
      };

      // if (isPrefillDataAvailable) {
      // FUTURE REFERENCE : removing because put does the post and update both api call from B.E
      const updatedScreener = tableData?.screeners?.map((it) => ({
        uuid: it?.uuid,
        question: it?.question,
        answer: it?.answerOptions,
      })) as any;

      showToastAndClose(
        updateScreeningAns({
          ...payload,
          patientScreener: {
            screenerQuestion: updatedScreener,
            appointmentId: appointmentid!,
            submittedBy: "PATIENT",
            screenerType: activeTab.val as any,
            providerName:
              providerName === "null" ? undefined : (providerName ?? ""),
            providerRole:
              providerRole === "null" ? undefined : (providerRole ?? ""),
            actionId: actionIdProp ?? undefined,
          },
        })
      );
      // } else showToastAndClose(saveScreeningAns(payload as any));
    }
  };

  // const [
  //   successScreening,
  //   { open: openSuccessScreening, close: closeSuccessScreening },
  // ] = useSuccess();

  const navigate = useNavigate();
  const showToastAndClose = (apiCall: Promise<any>) => {
    apiCall
      .then((res) => {
        if (
          activeTab.ind === (assessmentTabs && assessmentTabs?.length - 1) &&
          !actionIdProp
        ) {
          navigate("/public/submit-success");
          // openSuccessScreening();
        }
        showSnackbar(getSuccessSnackbar(res));
        refetch();

        if (actionIdProp)
          queryClient.invalidateQueries({
            queryKey: ["patient-requiring-action"],
          });

        onSave(activeTab.ind);
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  return (
    <Fragment>
      {/* <SuccessModal
        label={
          isPrefillDataAvailable
            ? "Successfully Updated Screening Form"
            : "Successfully Submitted Screening Form"
        }
        opened={successScreening}
        onClose={closeSuccessScreening}
      /> */}

      <LoadingOverlay isLoading={isLoading || isRefetching} />
      <Text mb={2} fontWeight={"bold"}>
        {DrawerTitleMapper[activeTab.val]}
      </Text>
      <ScrollArea height={"calc(100vh - 110px)"} padding={1}>
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {!tableData && !isLoading ? (
            <QnLoadFail />
          ) : (
            <AllScreeningAssessment
              tableData={tableData}
              handleRadioChange={handleRadioChange}
              titleDetail={TitleMapper[activeTab.val]}
            />
          )}
        </Box>
      </ScrollArea>
      <Flex justifyContent={"end"} width={"98%"} gap={1}>
        <Button variant="contained" onClick={saveHandler}>
          {activeTab.ind === (assessmentTabs && assessmentTabs?.length - 1)
            ? "Submit"
            : "Save & Next"}
        </Button>

        {assessmentTabs && assessmentTabs?.length > 1 && (
          <Button
            variant="outlined"
            onClick={() =>
              onSkip(
                activeTab.ind,
                activeTab.val === assessmentTabs?.[assessmentTabs?.length - 1]
              )
            }
          >
            {activeTab.val === assessmentTabs?.[assessmentTabs?.length - 1]
              ? "Prev"
              : "Next"}
          </Button>
        )}
      </Flex>
    </Fragment>
  );
};

const TitleMapper: Record<string, any> = {
  GAD7: {
    title: "GAD-7 (for Anxiety) - Please select one to the following",
    subtitle:
      "The Generalized Anxiety Disorder 7-item (GAD-7) is a easy to perform initial screening tool for generalized anxiety disorder.",
  },
  PHQ9: {
    title: "PHQ-9 (for Depression) - Please select one to the following",
    subtitle:
      "The PHQ-9 is a multipurpose instrument for screening, diagnosing,monitoring and measuring the severity of depression.",
  },
  MDQ: {
    title: "Mood Disorder Questionnaire (MDQ)",
    subtitle:
      "Employing the Mood Disorder Questionnaire (MDQ) to screen for potential mood disorders.",
  },
  CAGE: {
    title: "CAGE (for Alcohol) - Please answer Yes or No for the following",
    subtitle:
      "CAGE is a 4-item questionnaire that can indicate potential problems with alcohol use.",
  },
  SUBSTANCE_ABUSE: {
    title: "DAST-10 (for Substance Abuse)",
    subtitle:
      "The DAST-10 is a 10-item self-report tool used to screen for drug abuse and dependence within the past 12 months.",
  },
};

const DrawerTitleMapper: Record<string, string> = {
  GAD7: "GAD7",
  PHQ9: "PHQ9",
  SUBSTANCE_ABUSE: "Substance Abuse",
  CAGE: "Cage",
  MDQ: "MDQ",
};

const QnLoadFail = () => (
  <FlexCenterWrapper>
    <img src={noLoadQnImg} alt={"No Message Available"} />
    <Text fontSize={"16px"} mt={2}>
      Failed to load assessment questions. Please contact your healthcare
      provider for assistance.
    </Text>
  </FlexCenterWrapper>
);
